import { inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { Router } from '@angular/router';
import { AuthService } from '../core/auth/auth.service';
import { environment } from 'src/environments/environment';
import { SnackbarService } from '../services/snack-bar.service';
import { BrandingService } from '../services/branding.service';
import { HttpBackEnd } from '../services/httpBackend.service';

export const authInterceptor: HttpInterceptorFn = (request, next) => {
	const router = inject(Router);
	const auth = inject(AuthService);
	const snackbar = inject(SnackbarService);
	const brandingService = inject(BrandingService);
	const backendService = inject(HttpBackEnd);

	const token = auth.getToken();
	const modifiedRequest = request.clone({
		headers: token
			? request.headers
				.set('app-origin', environment.appOrigin)
				.set('Authorization', `Bearer ${token}`)
				.set('Cache-Control', 'no-cache')
			: request.headers
				.set('app-origin', environment.appOrigin)
				.set('Cache-Control', 'no-cache')
	});

	return next(modifiedRequest).pipe(
		map((event: HttpEvent<any>) => {
			backendService.setErrorRedirected(false);
			return event;
		}),
		catchError((err) => {
			let error: BackendError = { message: '', severity: ErrorSeverity.INFO, code: '' };

			if (err instanceof ErrorEvent) { // client side error
				error = handleUnknownError();
				snackbar.open('Error: ' + err.error.descripcion, 'error')

			} else { // server side error
				error = handleBackendError(error, err);
				if (err.status >= 500) {
					snackbar.open('An error happened accessing the RAP server. Please try again later.', 'error');
				} else if (err.status >= 400) {
					brandingService.updateGlobalStyles(undefined, 'reset', undefined);
					if (err.status == 401) {
						snackbar.open('You do not have sufficient permissions to perform this action.', 'error');
						auth.logout();
					} else if (err.status == 403) {
						snackbar.open('Your session expired, please log in again.', 'error');
						auth.logout();
					} else if (err.status == 429) {
						snackbar.open('Too many requests.', 'error');
						backendService.setErrorRedirected(true);
						router.navigate([`/error/${err.status}`]);
					} else {
						snackbar.open('An error happened. Please try again later.', 'error');
					}
				}
			}
			return throwError(() => error);
		})
	);
};

function handleUnknownError(): BackendError {
	// this is not from backend. Format our own message.
	return {
		message: 'Unknown error!',
		severity: ErrorSeverity.FATAL,
		code: 'UNKNOWN_ERROR',
	};
}

function handleBackendError(error: BackendError, err: any): BackendError {
	// Backend returned error, format it here
	return {
		title: err.error?.title || 'Error!',
		message:
			err.error && err.error.message
				? err.error.message
				: err.error
					? err.error
					: err.message,
		severity: ErrorSeverity.ERROR,
		code: err.error?.identifierCode
			? err.error.identifierCode
			: 'BACKEND_ERROR',
	};
}

export enum ErrorSeverity {
	INFO = 'INFO',
	WARNING = 'WARNING',
	ERROR = 'ERROR',
	FATAL = 'FATAL',
}

export interface BackendError {
	title?: string;
	message: string;
	severity: ErrorSeverity;
	code: string;
}
